import { motion } from 'framer-motion';
import { Star, TrendingUp } from 'lucide-react';

const testimonials = [
  {
    quote: "I'd been blogging for 3 years with decent traffic but zero video presence. AdoriAI changed that in a weekend. I uploaded 8 old posts and had 8 videos live on YouTube by Sunday night.",
    author: 'Sarah K.',
    role: 'Personal finance blogger · Austin, TX',
    image: 'https://images.unsplash.com/photo-1673767298248-b128f17f89af?w=100&h=100&fit=crop&crop=face',
  },
  {
    quote: "My YouTube channel went from 300 to 4,200 subscribers in 60 days. I didn't change my content strategy — I just started turning every post into a video with Adori. That's it.",
    author: 'Marcus R.',
    role: 'Tech blogger · London, UK',
    image: 'https://images.unsplash.com/photo-1647654034000-0010c45a569d?w=100&h=100&fit=crop&crop=face',
  },
  {
    quote: "I used to spend a full day making one video. Now it takes me 10 minutes. I publish 4–5 videos a week now. My old self would not believe it.",
    author: 'Priya M.',
    role: 'Lifestyle & travel blogger · Toronto',
    image: 'https://images.unsplash.com/photo-1610413352588-fe480e968b39?w=100&h=100&fit=crop&crop=face',
  },
  {
    quote: "The voiceovers are genuinely impressive. My audience keeps asking which 'presenter' I hired. When I tell them it's AI, they don't believe me.",
    author: 'Elena V.',
    role: 'Food & recipe blogger · Berlin',
    image: 'https://images.unsplash.com/photo-1758598305805-4b9d79ae89bf?w=100&h=100&fit=crop&crop=face',
  },
  {
    quote: "The SEO tools alone are worth the subscription. My videos rank on the first page for most of my keywords. New readers are finding me through YouTube now, not just Google.",
    author: 'Jake L.',
    role: 'Marketing & SEO blogger · NYC',
    image: 'https://i.pravatar.cc/100?img=33',
  },
];

const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="py-24 px-6 bg-surface-elevated/30" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            Creator Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Bloggers who stopped being
            <br />
            <span className="text-gray-400">invisible online</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Real creators. Real growth. No marketing fluff.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-surface rounded-2xl p-6 border border-white/10 hover:border-brand-primary/20 transition-colors"
            >
              {/* Stars */}
              <div className="flex text-yellow-400 mb-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              
              {/* Quote */}
              <p className="text-gray-300 leading-relaxed mb-6">"{testimonial.quote}"</p>
              
              {/* Author */}
              <div className="flex items-center gap-3">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-white font-semibold">{testimonial.author}</div>
                  <div className="text-gray-500 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Stats Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-brand-primary/20 to-surface rounded-2xl p-6 border border-brand-primary/30 flex flex-col justify-center items-center text-center"
          >
            <TrendingUp className="w-10 h-10 text-brand-primary mb-4" />
            <div className="text-5xl font-heading font-black text-white mb-2">95%</div>
            <div className="text-lg font-semibold text-white mb-2">More message retention</div>
            <p className="text-gray-400 text-sm">
              Viewers remember 95% of your message in video vs. just 10% in text. 
              Adori users see 3–5× more engagement on repurposed content.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
